import { makeAuthenticatedRequest } from '../utils/tokenUtils';

/**
 * Function to invoke the Chain API.
 * 
 * @param {string} user_id - The user ID of the requester.
 * @param {string} model - The model to be used (e.g., "gpt-4o-mini").
 * @param {string} system_message - System instructions for the model.
 * @param {string} prompt - The main prompt for the model.
 * @param {number} temperature - Sampling temperature for response randomness (e.g., 0.1).
 * @param {boolean} stream - Whether to stream the response.
 * @param {number} max_tokens - Maximum number of tokens allowed in the response (e.g., 8192).
 * @param {string} conversation_id - The ID of the ongoing conversation (if any).
 * @param {string} response_format - The desired format of the response (e.g., "text").
 * @param {string} answer_type - The type of response expected (e.g., "case_study").
 * @param {Array<Object>} subjects - A list of subjects for context.
 * @param {string} subjects[].subject - The name of the subject.
 * @param {number} subjects[].weightage - The weightage or importance of the subject.
 * 
 * @returns {Promise} - A promise that resolves to the API response.
 */
export const invokeChainAPI = async ({
  context,
  user_id,
  model,
  system_message,
  prompt,
  temperature,
  stream,
  max_tokens,
  conversation_id,
  response_format,  
  answer_type,
  subjects,
  messageId
}) => {
  const url = process.env.REACT_APP_API_URL + 'llms/mentormind';
  const requestBody = {
    context,
    user_id,
    model,
    system_message,
    prompt,
    temperature,
    stream,
    max_tokens,
    conversation_id,
    response_format,
    answer_type,
    subjects,
    messageId
  };

  try {
    const response = await makeAuthenticatedRequest(url, {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });

    if (stream) {
      return response.body.getReader(); // Return the reader
    } else {
      return response.body; // Return the JSON
    }
  } catch (error) {
    console.error('Error fetching stream:', error);
    throw error;
  }
};

export const fetchCaseStudy = async ({ message_id, topic, subject, answer_type, query }) => {
  const url = process.env.REACT_APP_API_URL + 'llms/case-study';
  const response = await makeAuthenticatedRequest(url, {
    method: 'POST',
    body: JSON.stringify({ message_id, topic, subject, answer_type, query }),
  });
  return response.json();
};

export const fetchInitiatives = async ({ message_id, topic, subject, answer_type, query }) => {
  const url = process.env.REACT_APP_API_URL + 'llms/initiatives';
  const response = await makeAuthenticatedRequest(url, {
    method: 'POST',
    body: JSON.stringify({ message_id, topic, subject, answer_type, query }),
  });
  return response.json();
};

export default invokeChainAPI;
